import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Bell, Sparkles, Phone, MapPin, Calendar as CalIcon, MessageSquare, Mail, StickyNote, FileText, MoreHorizontal, Lightbulb, Loader2, ArrowRight, ThumbsUp, ThumbsDown, AlertCircle, CheckCircle2, Circle, ChevronDown, UserCheck, Bot } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICON = {
  call: Phone, visit: MapPin, appointment: CalIcon, message: MessageSquare,
  email: Mail, note: StickyNote, brochure: FileText, other: MoreHorizontal,
  // Punto 4 (coach-ia-historial-likes, lote 2): registro automático de
  // toma/retorno de control del Agente IA -- tipos de SISTEMA, no
  // seleccionables en "Registrar actividad".
  takeover: UserCheck, handoff: Bot,
};

// Pieza 3: mismos tipos/labels del modal "Registrar actividad"
// (RegisterActivityDialog.jsx). Sin Icon aquí — el select no los necesita.
const ACTIVITY_TYPE_OPTIONS = [
  { key: 'call',        label: 'Llamada' },
  { key: 'visit',       label: 'Visita presencial' },
  { key: 'appointment', label: 'Cita / Reunión' },
  { key: 'message',     label: 'Mensaje WhatsApp' },
  { key: 'email',       label: 'Correo' },
  { key: 'note',        label: 'Nota seguimiento' },
  { key: 'brochure',    label: 'Envío ficha' },
  { key: 'other',       label: 'Otra' },
  // Punto 4: filtrables aunque no sean creables a mano (mismo criterio que
  // el tab "Informativas" -- nacen sin `status`, visibles ahí/Todas/Hoy).
  { key: 'takeover',    label: 'Asesor tomó control' },
  { key: 'handoff',     label: 'Asesor devolvió a IA' },
];

// Ticket coach-simplificacion: chips de contexto rápido para "¿Qué hago
// ahora?" — reemplazan el textarea libre. `phrase` arma la frase que se
// manda como `context` al backend; `label` es lo que ve el vendedor.
const CONTEXT_CHIPS = [
  { key: 'no_contesta',       label: 'No contesta',                     phrase: 'no contesta' },
  { key: 'interes_frio',      label: 'Se enfrió el interés',            phrase: 'se enfrió el interés' },
  { key: 'objecion_precio',   label: 'Objeción de precio',              phrase: 'objeción de precio' },
  { key: 'listo_cerrar',      label: 'Listo para cerrar',               phrase: 'listo para cerrar' },
  { key: 'primera_vez',       label: 'Primera vez que contacto',        phrase: 'primera vez que contacto' },
  { key: 'cliente_exigente',  label: 'Cliente exigente / muchas dudas', phrase: 'cliente exigente / muchas dudas' },
];

// Punto 6: created_at es UTC puro (_now_iso() del backend) — mostrarlo
// crudo desfasa la hora ~6h vs. México. Mismo patrón exacto que `fmtFecha`
// en RadiografiaPanel.jsx (no exportado ahí, se replica local en vez de
// crear un módulo compartido nuevo).
const fmtFecha = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'America/Mexico_City', hour12: false,
    });
  } catch {
    return String(iso);
  }
};

// Cambio 2 (coach-simplificacion): coachText llega como texto plano con
// viñetas markdown ("- algo"). Se parsea a líneas para pintar cada viñeta
// con ícono en vez del guión crudo; una línea sin guión (ej. intro del
// modelo) se pinta como párrafo simple, sin ícono.
const parseCoachLines = (text) => {
  return (text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const isBullet = /^[-•*]\s+/.test(l);
      return { text: l.replace(/^[-•*]\s+/, ''), isBullet };
    });
};

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

export default function ActivitiesBellDialog({ open, onOpenChange, user, products = [], groups = [], vendors = [], leads = [], leadId = null, leadName = '' }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <ActivitiesBellInner
          onOpenChange={onOpenChange}
          user={user}
          products={products}
          vendors={vendors}
          leads={leads}
          leadId={leadId}
          leadName={leadName}
        />
      )}
    </Dialog>
  );
}

// Pieza 3: `groups` ya NO se recibe aquí (el filtro de Grupo se retiró del
// panel) — sigue llegando a `ActivitiesBellDialog` desde OpportunitiesPage.js
// sin tocar ese archivo (prop no reenviado, no rompe nada), solo se deja de
// consumir internamente.
// B2: `leadId`/`leadName` (opcionales) fijan el panel a UN lead — reusa este
// mismo componente en vez de duplicar tabs/checkbox/filtros en uno nuevo.
function ActivitiesBellInner({ onOpenChange, user, products, vendors, leads, leadId, leadName }) {
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  // Punto 8: created_at (registro) vs appointment_date (programada) para
  // el rango de "Buscar por fecha". Default created_at -- comportamiento
  // sin cambios si el usuario no toca el selector.
  const [dateField, setDateField] = useState('created_at');
  const [productId, setProductId] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [vendorId, setVendorId] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all'); // all|today|pending|done|range
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachText, setCoachText] = useState('');
  // Cambio 3: metadata del caché por-lead (null cuando no hay leadId, o
  // cuando el panel general/campanita generó algo sin persistir). `stale`
  // = hubo actividad nueva desde que se generó -- ver GET /activities/
  // coach-suggestion. `feedback`/`generatedBy` alimentan el gate del 👍/👎
  // (Cambio 4: solo quien generó puede calificar).
  const [coachMeta, setCoachMeta] = useState(null);
  const [coachFeedbackSaving, setCoachFeedbackSaving] = useState(false);
  const [selectedChips, setSelectedChips] = useState([]);
  // Punto 3: "ver más/menos" por fila — toggle local, sin backend.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Ticket coach-ia-historial-likes: historial acumulado de consejos con
  // 👍 -- independiente de coachText/coachMeta (el consejo ACTUAL puede
  // vaciarse por un 👎 sin que sus tags previos desaparezcan, punto 3).
  const [likesHistory, setLikesHistory] = useState([]);
  const [expandedLikeIds, setExpandedLikeIds] = useState(() => new Set());
  const [showAllLikes, setShowAllLikes] = useState(false);
  // Punto 1 (lote 2): sección plegable, colapsada por default -- ya no vive
  // dentro de "bell-coach-panel" (ver el JSX, movida a su propia fila).
  const [likesHistoryOpen, setLikesHistoryOpen] = useState(false);

  // Backend es la autoridad (ver activities.py::list_activities) — esto
  // solo evita mandar una intención engañosa y ocultar controles que el
  // backend igual ignoraría para estos roles. vendor/guard: forzados a lo
  // suyo, igual que _vendor_scope_lead_ids en routes/ai_agent.py.
  const isForcedScope = user?.role === 'vendor' || user?.role === 'guard';
  const allowedModules = user?.allowed_modules || user?.company?.allowed_modules || {};
  const showProducts = allowedModules.products !== false && products?.length > 0;

  const leadNameById = useMemo(() => {
    const map = new Map();
    for (const l of leads || []) {
      if (l?.id && l?.name) map.set(l.id, l.name);
    }
    return map;
  }, [leads]);

  // Pieza 5: assigned_to del lead (para resolver el nombre del vendedor) +
  // catálogo de vendedores. Mismo patrón que leadNameById — mapas O(1) sobre
  // los catálogos que OpportunitiesPage.js ya carga, sin llamada nueva.
  const leadAssignedToById = useMemo(() => {
    const map = new Map();
    for (const l of leads || []) {
      if (l?.id) map.set(l.id, l.assigned_to);
    }
    return map;
  }, [leads]);

  const teamById = useMemo(() => {
    const map = new Map();
    for (const v of vendors || []) {
      if (v?.id) map.set(v.id, v);
    }
    return map;
  }, [vendors]);

  // Guardia del polimorfismo leads.assigned_to (CLAUDE.md §4): string|array|
  // null. Solo un string no vacío resuelve; cualquier otra forma o un id que
  // no matchea en el catálogo de vendedores devuelve '' (nunca se pinta el
  // array crudo ni el id).
  const resolveVendorName = (leadId) => {
    const assignedTo = leadId ? leadAssignedToById.get(leadId) : null;
    if (typeof assignedTo !== 'string' || !assignedTo.trim()) return '';
    return teamById.get(assignedTo)?.name || '';
  };

  const params = useMemo(() => {
    const p = { limit: 100 };
    // scope='mine' solo se manda para vendor/guard — el backend lo fuerza
    // igual por rol, pero no hay razón para mandar una intención engañosa
    // a un admin/gerente (que debe ver TODA la empresa por defecto).
    if (isForcedScope) p.scope = 'mine';
    // Pieza 4: el date-range ya NO acompaña a ningún otro tab — solo se
    // manda cuando "Buscar por fecha" está activa. Todas/Hoy/Pendientes/
    // Realizadas quedan con su propio criterio, sin arrastrar el mes actual.
    if (timeFilter === 'range') {
      p.date_from = dateFrom;
      p.date_to = dateTo;
      p.date_field = dateField;
    } else if (timeFilter === 'today') {
      p.time_filter = 'today';
    } else if (timeFilter === 'pending' || timeFilter === 'done') {
      // Pieza 2/3: status real marcado por el vendedor, no una fecha.
      p.status = timeFilter;
    } else if (timeFilter === 'informative') {
      // A3: sentinela 'none' -- note/brochure, las únicas sin status.
      p.status = 'none';
    }
    if (!isForcedScope && showProducts && productId && productId !== 'all') p.product_id = productId;
    if (typeFilter && typeFilter !== 'all') p.type = typeFilter;
    if (!isForcedScope && !leadId && vendorId && vendorId !== 'all') {
      p.vendor_id = vendorId;
    }
    // B2: panel fijado a UN lead -- filtro duro, no editable por el usuario.
    if (leadId) p.lead_id = leadId;
    return p;
  }, [dateFrom, dateTo, dateField, productId, typeFilter, vendorId, isForcedScope, showProducts, timeFilter, leadId]);

  useEffect(() => {
    let cancel = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get('/activities', { params });
        if (!cancel) {
          setItems(res.data?.items || []);
          setPendingCount(res.data?.pending_count || 0);
        }
      } catch (e) {
        if (!cancel) { setItems([]); setPendingCount(0); }
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchData();
    return () => { cancel = true; };
  }, [params]);

  // Cambio 3: precarga del último consejo cacheado -- SOLO panel B2 (fijado
  // a un lead). Sin llamada al LLM (GET dedicado); el vendedor lo ve al
  // abrir, sin pulsar nada. Panel general/campanita (sin leadId) no cachea:
  // se queda igual que antes (coachText vacío hasta pulsar el botón).
  useEffect(() => {
    if (!leadId) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get('/activities/coach-suggestion', { params: { lead_id: leadId } });
        if (cancel || !res.data?.cached) return;
        setCoachText(res.data.suggestion || '');
        setCoachMeta({
          stale: !!res.data.stale,
          generatedAt: res.data.generated_at || null,
          generatedBy: res.data.generated_by || null,
          model: res.data.model || null,
          feedback: res.data.feedback || null,
        });
      } catch (e) {
        // Precarga best-effort -- el botón "Ver consejo" sigue funcionando.
      }
    })();
    return () => { cancel = true; };
  }, [leadId]);

  // Ticket coach-ia-historial-likes, punto 6: mismo scope que el resto de
  // endpoints de Coach IA (_resolve_lead_for_coach en backend) -- SOLO B2.
  useEffect(() => {
    if (!leadId) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get('/activities/coach-suggestion/history', { params: { lead_id: leadId } });
        if (!cancel) setLikesHistory(res.data?.items || []);
      } catch (e) {
        // Precarga best-effort -- sin historial no rompe el panel.
      }
    })();
    return () => { cancel = true; };
  }, [leadId]);

  // Chips seleccionados -> "El vendedor indica: a, b, c". Sin selección,
  // mismo fallback que existía con el textarea vacío (nombre + rango).
  const toggleChip = (key) => {
    setSelectedChips(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  // Cambio 3: con leadId + ya hay algo pintado (cache precargado o consejo
  // recién generado), el botón pasa a forzar regeneración -- sin leadId el
  // comportamiento es idéntico al de siempre (siempre genera).
  const coachHasCached = Boolean(leadId && coachText);

  const handleCoach = async () => {
    if (!items.length) {
      toast.error('No hay actividades para analizar.');
      return;
    }
    setCoachLoading(true);
    try {
      const chipsPhrase = selectedChips
        .map(key => CONTEXT_CHIPS.find(c => c.key === key)?.phrase)
        .filter(Boolean)
        .join(', ');
      const res = await api.post('/activities/coach-suggestions', {
        activities: items.slice(0, 30),
        context: chipsPhrase
          ? `El vendedor indica: ${chipsPhrase}`
          : `${user?.name || 'Vendedor'} · ${dateFrom} a ${dateTo}`,
        ...(leadId ? { lead_id: leadId, force: coachHasCached } : {}),
      });
      if (res.data?.ok) {
        setCoachText(res.data.suggestions);
        if (leadId) {
          setCoachMeta({
            stale: false,
            generatedAt: res.data.generated_at || null,
            generatedBy: res.data.generated_by || user?.id || null,
            model: res.data.model || null,
            feedback: res.data.feedback || null,
          });
        }
      } else {
        toast.warning(res.data?.suggestions || 'No fue posible generar sugerencias.');
        setCoachText(res.data?.suggestions || '');
        if (leadId) setCoachMeta(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al consultar el coach IA');
    } finally {
      setCoachLoading(false);
    }
  };

  // Cambio 4: 👍/👎 -- solo visible/habilitado para quien generó el consejo
  // (decisión de producto). Optimista, revierte si el PATCH falla.
  const canRateCoach = Boolean(leadId && coachMeta && coachMeta.generatedBy === user?.id);

  const handleCoachFeedback = async (rating) => {
    if (!leadId || coachFeedbackSaving) return;
    const prevFeedback = coachMeta?.feedback || null;
    setCoachFeedbackSaving(true);
    // Punto C: 👎 va a vaciar el panel entero -- no tiene sentido
    // optimizar un "feedback" que está a punto de desaparecer. Solo el
    // 👍 se refleja optimista.
    if (rating === 'up') {
      setCoachMeta(prev => (prev ? { ...prev, feedback: { rating } } : prev));
    }
    try {
      const res = await api.patch('/activities/coach-suggestion/feedback', {
        lead_id: leadId,
        rating,
        generated_at: coachMeta?.generatedAt || undefined,
      });
      if (rating === 'down') {
        // Punto C: el consejo deja de existir en el backend -- se limpia
        // local sin esperar a reabrir el panel. El historial de tags
        // (👍 previos) NO se toca, es independiente del consejo actual.
        setCoachText('');
        setCoachMeta(null);
      } else {
        setCoachMeta(prev => (prev ? { ...prev, feedback: res.data?.feedback || { rating } } : prev));
        // Pieza 1: el tag aparece de inmediato, sin esperar a reabrir el
        // panel. Dedup por generated_at -- mismo criterio del upsert que
        // hace el backend (un like repetido sobre el mismo consejo
        // reemplaza su entrada, no la duplica).
        // Punto 3: usa el `like_id` REAL que ahora devuelve el backend (no
        // un `local-...` temporal) -- el botón "usado" de este mismo tag
        // pega directo a ese id, sin esperar a que un refetch lo reemplace.
        setLikesHistory(prev => {
          const generatedAt = coachMeta?.generatedAt || null;
          const entry = {
            id: res.data?.like_id || `local-${generatedAt || Date.now()}`,
            text: coachText,
            generated_at: generatedAt,
            liked_at: res.data?.feedback?.rated_at || new Date().toISOString(),
            used: false,
          };
          return [entry, ...prev.filter(i => i.generated_at !== generatedAt)];
        });
      }
    } catch (e) {
      if (rating === 'up') {
        setCoachMeta(prev => (prev ? { ...prev, feedback: prevFeedback } : prev));
      }
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la calificación');
    } finally {
      setCoachFeedbackSaving(false);
    }
  };

  // Pieza 1: expandir/colapsar un tag del historial -- mismo patrón que
  // toggleExpand (descripciones de actividad), Set local sin backend.
  const toggleLikeExpand = (id) => {
    setExpandedLikeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Punto 3: marca/desmarca un tag como "utilizado" -- optimista, revierte
  // si el PATCH falla. Entradas `local-...` (like_id no llegó del backend
  // por algún fallo de red en el 👍) no tienen contraparte real: se
  // ignoran en vez de mandar un PATCH que va a dar 404.
  const [likeUsedSaving, setLikeUsedSaving] = useState(() => new Set());
  const toggleLikeUsed = async (id) => {
    if (id.startsWith('local-') || likeUsedSaving.has(id)) return;
    const prevUsed = likesHistory.find(i => i.id === id)?.used || false;
    setLikeUsedSaving(prev => new Set(prev).add(id));
    setLikesHistory(prev => prev.map(i => (i.id === id ? { ...i, used: !prevUsed } : i)));
    try {
      await api.patch(`/activities/coach-suggestion/history/${id}/used`);
    } catch (e) {
      setLikesHistory(prev => prev.map(i => (i.id === id ? { ...i, used: prevUsed } : i)));
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setLikeUsedSaving(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Punto 3: toggle local de expandir/colapsar descripción larga.
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Pieza 2/3: checkbox Pendiente<->Realizada. Optimista — refleja el
  // cambio de inmediato en la lista local (sin refetch) y revierte si el
  // PATCH falla. Solo aplica a tipos accionables (a.status presente).
  const handleToggleStatus = async (activity) => {
    if (activity.status !== 'pending' && activity.status !== 'done') return;
    const nextStatus = activity.status === 'done' ? 'pending' : 'done';
    const prevStatus = activity.status;
    setItems(prev => prev.map(i => (i.id === activity.id ? { ...i, status: nextStatus } : i)));
    try {
      await api.patch(`/activities/${activity.id}`, { status: nextStatus });
    } catch (e) {
      setItems(prev => prev.map(i => (i.id === activity.id ? { ...i, status: prevStatus } : i)));
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar el estado');
    }
  };

  return (
    <DialogContent
      className={`${leadId ? 'max-w-4xl' : 'max-w-3xl'} bg-card border-white/10 max-h-[90vh] overflow-hidden flex flex-col`}
      data-testid="activities-bell-dialog"
    >
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-400" />
          {leadId ? `Actividades de ${leadName || 'este lead'}` : 'Mis actividades'}
        </DialogTitle>
        <DialogDescription className="text-muted-foreground text-xs">
          Bitácora del periodo. Genera sugerencias accionables con IA.
        </DialogDescription>
      </DialogHeader>

      {/* Filtros */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="bell-filters">
        {!isForcedScope && showProducts && (
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Producto</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="h-8 mt-1 bg-black/20 border-white/10 text-xs" data-testid="bell-product"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name || p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
        )}
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Tipo</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 mt-1 bg-black/20 border-white/10 text-xs" data-testid="bell-type"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ACTIVITY_TYPE_OPTIONS.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!isForcedScope && !leadId && (
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Vendedor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger className="h-8 mt-1 bg-black/20 border-white/10 text-xs" data-testid="bell-vendor"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
        )}
      </div>

      {/* Tabs de estado/tiempo */}
      <div className="flex flex-wrap gap-1.5" data-testid="bell-time-tabs">
        {[
          { key: 'all',     label: 'Todas' },
          { key: 'today',   label: 'Hoy' },
          { key: 'pending', label: `Pendientes${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'done',    label: 'Realizadas' },
          { key: 'informative', label: 'Informativas' },
          { key: 'range',   label: 'Buscar por fecha' },
        ].map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTimeFilter(t.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              timeFilter === t.key
                ? 'bg-amber-500/20 border-amber-500/40 text-amber-200'
                : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
            }`}
            data-testid={`bell-time-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {/* Pieza 4: date-range SOLO bajo "Buscar por fecha" — desacoplado por
          completo de Todas/Hoy/Pendientes/Realizadas. */}
      {timeFilter === 'range' && (
        <div className="space-y-2" data-testid="bell-date-range-fields">
          {/* Punto 8: created_at (registro) vs appointment_date (programada) */}
          <div className="grid grid-cols-2 gap-2" data-testid="bell-date-field-toggle">
            <button
              type="button"
              onClick={() => setDateField('created_at')}
              className={`p-1.5 rounded-lg border text-[11px] transition-colors ${
                dateField === 'created_at'
                  ? 'bg-primary/15 border-primary/40 text-primary'
                  : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/15'
              }`}
              data-testid="bell-date-field-created"
            >
              Fecha de registro
            </button>
            <button
              type="button"
              onClick={() => setDateField('appointment_date')}
              className={`p-1.5 rounded-lg border text-[11px] transition-colors ${
                dateField === 'appointment_date'
                  ? 'bg-primary/15 border-primary/40 text-primary'
                  : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/15'
              }`}
              data-testid="bell-date-field-appointment"
            >
              Fecha programada
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 mt-1 bg-black/20 border-white/10 text-xs" data-testid="bell-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 mt-1 bg-black/20 border-white/10 text-xs" data-testid="bell-date-to" />
            </div>
          </div>
        </div>
      )}

      {/* Punto 1 (coach-ia-historial-likes, lote 2): sección propia,
          plegable, colapsada por default -- FUERA de la columna del Coach
          a propósito. Antes vivía dentro de "bell-coach-panel" y expandir
          un chip empujaba los chips de contexto/botón/respuesta de ese
          mismo panel (bug reportado tras validar en LAB). Al ser una fila
          de ancho completo, independiente del grid de abajo, expandir un
          tag aquí nunca toca el Coach ni la proporción del Punto B. */}
      {leadId && likesHistory.length > 0 && (
        <div className="border border-emerald-500/20 rounded-lg bg-emerald-500/5" data-testid="bell-coach-likes-history">
          <button
            type="button"
            onClick={() => setLikesHistoryOpen(prev => !prev)}
            className="w-full px-3 py-2 flex items-center justify-between text-xs text-white font-medium"
            data-testid="bell-coach-likes-history-toggle"
          >
            <span className="flex items-center gap-1.5">
              <ThumbsUp className="w-3.5 h-3.5 text-emerald-300" />
              Consejos que te sirvieron antes ({likesHistory.length})
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${likesHistoryOpen ? 'rotate-180' : ''}`} />
          </button>
          {/* Punto 1 (lote 3): max-h + overflow-y-auto -- confirmado con el
              CSS real que esta sección no tenía ningún límite de altura;
              con el historial Y la respuesta del Coach expandidos a la vez,
              el contenido superaba el max-h-[90vh] del dialog y se
              renderizaba encima del footer ("Cerrar" tapado). Mismo patrón
              de overflow que el resto del componente (ScrollArea), aplicado
              aquí como un simple overflow-y-auto porque es contenido corto
              y no necesita la barra de scroll estilizada de Radix. */}
          {likesHistoryOpen && (
            <div className="px-3 pb-3 max-h-[280px] overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {(showAllLikes ? likesHistory : likesHistory.slice(0, 5)).map(item => {
                  const isExpanded = expandedLikeIds.has(item.id);
                  const firstLine = parseCoachLines(item.text)[0]?.text || item.text || '';
                  const shortLabel = firstLine.length > 45 ? `${firstLine.slice(0, 45)}…` : firstLine;
                  return (
                    <div key={item.id} className="w-full sm:w-auto">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleLikeExpand(item.id)}
                          className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                            isExpanded
                              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200'
                              : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
                          }`}
                          data-testid={`bell-coach-like-chip-${item.id}`}
                        >
                          👍 {shortLabel}{item.liked_at ? ` · ${fmtFecha(item.liked_at)}` : ''}
                        </button>
                        {/* Punto 3: toggle "utilizado" -- sin contador todavía. */}
                        <button
                          type="button"
                          onClick={() => toggleLikeUsed(item.id)}
                          title={item.used ? 'Marcado como utilizado' : 'Marcar como utilizado'}
                          className={`p-1 rounded-full border transition-colors flex-shrink-0 ${
                            item.used
                              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                              : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
                          }`}
                          data-testid={`bell-coach-like-used-${item.id}`}
                        >
                          {item.used ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="mt-1.5 bg-black/25 border border-emerald-500/20 rounded-lg p-2.5 space-y-1.5">
                          {parseCoachLines(item.text).map((line, i) => (
                            line.isBullet ? (
                              <div key={i} className="flex items-start gap-2">
                                <ArrowRight className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-white/90 leading-relaxed">{line.text}</p>
                              </div>
                            ) : (
                              <p key={i} className="text-xs text-white/70 leading-relaxed">{line.text}</p>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!showAllLikes && likesHistory.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllLikes(true)}
                    className="px-2.5 py-1 rounded-full text-[11px] border bg-black/20 border-white/10 text-muted-foreground hover:border-white/20"
                    data-testid="bell-coach-likes-more"
                  >
                    +{likesHistory.length - 5} más
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Punto A: sin leadId (panel general/campanita) no hay columna de
          Coach que mostrar -- una sola columna a ancho completo. Punto 2
          (lote 3): el ratio 50/50 YA NO depende de `coachText` -- antes
          saltaba a 1:1.2 al generar un consejo (y el dialog saltaba de
          max-w-3xl a max-w-4xl al mismo tiempo, arriba) = doble salto
          visual reportado por Alex. Ahora ambos (ancho del dialog + ratio
          de columnas) son fijos mientras haya `leadId`, sin importar si
          hay consejo visible -- cierra el salto de raíz, no lo disimula. */}
      <div className={`grid grid-cols-1 gap-3 flex-1 min-h-0 ${leadId ? 'md:grid-cols-2' : ''}`}>
        {/* Lista de actividades */}
        <div className="border border-white/5 rounded-lg bg-black/20 flex flex-col min-h-0" data-testid="bell-activities-list">
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <p className="text-xs text-white font-medium">Actividades ({items.length})</p>
          </div>
          <ScrollArea className="flex-1 min-h-0">
            {loading ? (
              <p className="text-xs text-muted-foreground p-3">Cargando…</p>
            ) : !items.length ? (
              <p className="text-xs text-muted-foreground p-3" data-testid="bell-empty">Sin actividades en el rango.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map(a => {
                  const Icon = TYPE_ICON[a.type] || MoreHorizontal;
                  const leadName = a.lead_id ? leadNameById.get(a.lead_id) : '';
                  const title = leadName ? `${leadName} — ${a.label || a.type}` : (a.label || a.type);
                  const hasStatus = a.status === 'pending' || a.status === 'done';
                  // Pieza 5: solo para roles privilegiados — un vendor/guard
                  // ya sabe que las actividades que ve son suyas.
                  const vendorName = !isForcedScope ? resolveVendorName(a.lead_id) : '';
                  return (
                    <li key={a.id} className={`px-3 py-2 text-xs flex items-start gap-2 ${a.status === 'done' ? 'bg-emerald-500/10 border-l-2 border-emerald-500/40' : ''}`} data-testid={`bell-act-${a.id}`}>
                      {hasStatus && (
                        <Checkbox
                          checked={a.status === 'done'}
                          onCheckedChange={() => handleToggleStatus(a)}
                          className="mt-0.5 flex-shrink-0"
                          data-testid={`bell-act-status-${a.id}`}
                        />
                      )}
                      <Icon className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{title}</p>
                        {/* Punto 6: fmtFecha convierte de UTC a hora de México
                            -- antes se mostraba el string UTC crudo. */}
                        <p className="text-muted-foreground text-[10px]">{fmtFecha(a.created_at)}</p>
                        {/* Punto 2: fecha/hora programada, solo si existe (los
                            6 tipos accionables) -- appointment_date es fecha
                            de calendario plana, sin conversión de zona. */}
                        {a.appointment_date && (
                          <p className="text-muted-foreground text-[10px]">
                            Programada: {a.appointment_date}{a.appointment_time ? ` ${a.appointment_time}` : ''}
                          </p>
                        )}
                        {/* Pieza 1: ubicación, solo visita/cita (las únicas
                            que la capturan) y solo si viene no vacía. */}
                        {a.location && <p className="text-muted-foreground text-[10px]">Ubicación: {a.location}</p>}
                        {vendorName && <p className="text-muted-foreground text-[10px]">Asesor: {vendorName}</p>}
                        {/* Punto 5: quién la marcó realizada, sin gate por tab. */}
                        {a.status === 'done' && a.completed_by_name && (
                          <p className="text-emerald-400/80 text-[10px]">Marcada como realizada por {a.completed_by_name}</p>
                        )}
                        {a.description && (
                          <>
                            <p className={`text-white/70 text-[11px] mt-0.5 ${expandedIds.has(a.id) ? '' : 'line-clamp-2'}`}>{a.description}</p>
                            {a.description.length > 80 && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(a.id)}
                                className="text-[10px] text-primary hover:underline"
                                data-testid={`bell-act-expand-${a.id}`}
                              >
                                {expandedIds.has(a.id) ? 'ver menos' : 'ver más'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </div>

        {/* Coach IA — Punto A: solo con leadId (B2). El panel general/
            campanita ya no mezcla actividades de leads distintos. */}
        {leadId && (
        <div className="border border-purple-500/20 rounded-lg bg-purple-500/5 flex flex-col min-h-0" data-testid="bell-coach-panel">
          <div className="px-3 py-2 border-b border-purple-500/15 flex items-center justify-between">
            <p className="text-xs text-white font-medium flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-purple-300" /> Tu siguiente paso</p>
            <Button size="sm" variant="ghost" onClick={handleCoach} disabled={coachLoading || !items.length} className="h-7 text-xs" data-testid="bell-coach-generate">
              {coachLoading
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <Lightbulb className="w-3 h-3 mr-1" />}
              {coachLoading
                ? 'Analizando…'
                : !coachHasCached
                  ? 'Ver consejo'
                  : (coachMeta?.stale ? 'Actualizar consejo' : 'Actualizar')}
            </Button>
          </div>
          <div className="p-3 space-y-2">
            <p className="text-[10px] uppercase text-muted-foreground">¿Cómo va este cliente? (opcional)</p>
            <div className="flex flex-wrap gap-1.5" data-testid="bell-coach-chips">
              {CONTEXT_CHIPS.map(c => {
                const active = selectedChips.includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => toggleChip(c.key)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                      active
                        ? 'bg-purple-500/20 border-purple-500/40 text-purple-200'
                        : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
                    }`}
                    data-testid={`bell-coach-chip-${c.key}`}
                  >{c.label}</button>
                );
              })}
            </div>
          </div>
          {/* Punto 2 (lote 2): min-h fijo para que el salto entre "vacío" y
              "con consejo" (al dar 👎 o al generar uno nuevo) no se sienta
              brusco -- mismo patrón que la tarjeta de respuesta IA de
              AICampaignIndependentPage.js:697 (min-h-[96px] en un caso más
              corto; acá 200px porque el consejo trae hasta 6 viñetas). */}
          <ScrollArea className="flex-1 min-h-[200px] px-3 pb-3">
            {!coachText ? (
              <p className="text-xs text-muted-foreground">Pulsa &ldquo;Ver consejo&rdquo; para obtener sugerencias accionables.</p>
            ) : (
              <div className="space-y-2">
                {/* Cambio 3: consejo cacheado pero ya hay actividad nueva del
                    lead -- se sigue mostrando (mejor que en blanco), con
                    aviso de que puede estar desactualizado. */}
                {coachMeta?.stale && (
                  <p className="flex items-center gap-1.5 text-[11px] text-amber-300/90" data-testid="bell-coach-stale">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    Este es el último consejo generado -- hay actividad nueva desde entonces.
                  </p>
                )}
                <div className="bg-black/25 border border-purple-500/20 rounded-xl p-4 space-y-2.5" data-testid="bell-coach-text">
                  {parseCoachLines(coachText).map((line, i) => (
                    line.isBullet ? (
                      <div key={i} className="flex items-start gap-2.5">
                        <ArrowRight className="w-4 h-4 text-purple-300 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-white/90 leading-relaxed">{line.text}</p>
                      </div>
                    ) : (
                      <p key={i} className="text-sm text-white/70 leading-relaxed">{line.text}</p>
                    )
                  ))}
                </div>
                {/* Cambio 4: 👍/👎, solo para quien generó este consejo. */}
                {canRateCoach && (
                  <div className="flex items-center gap-1.5 pl-1" data-testid="bell-coach-feedback">
                    <span className="text-[10px] text-muted-foreground">¿Te sirvió?</span>
                    <button
                      type="button"
                      onClick={() => handleCoachFeedback('up')}
                      disabled={coachFeedbackSaving}
                      className={`p-1 rounded-md border transition-colors ${
                        coachMeta?.feedback?.rating === 'up'
                          ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                          : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
                      }`}
                      data-testid="bell-coach-feedback-up"
                    >
                      <ThumbsUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCoachFeedback('down')}
                      disabled={coachFeedbackSaving}
                      className={`p-1 rounded-md border transition-colors ${
                        coachMeta?.feedback?.rating === 'down'
                          ? 'bg-red-500/20 border-red-500/40 text-red-300'
                          : 'bg-black/20 border-white/10 text-muted-foreground hover:border-white/20'
                      }`}
                      data-testid="bell-coach-feedback-down"
                    >
                      <ThumbsDown className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
        )}
      </div>

      {/* Punto 1 (lote 3): flex-shrink-0 -- refuerzo defensivo. Sin esto,
          es el elemento más débil del flex-column cuando algo más crece
          (el historial expandido, antes sin límite); con el max-h del
          historial ya puesto, esto es cinturón-y-tirantes, no el fix
          principal, pero barato y evita que vuelva a ser el que se tape. */}
      <div className="flex justify-end pt-2 border-t border-white/5 flex-shrink-0">
        <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="bell-close">Cerrar</Button>
      </div>
    </DialogContent>
  );
}
